import React, { useState, useMemo } from "react";
import { Flex, FormDialog, TextField, Typography, Box, FormControl } from "flux";
import useMutateUser from "../../hooks/use-mutate-user";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import { User } from "../../types/user";
import { FormControlLabel, Checkbox, FormGroup } from "@mui/material";
import { z } from "zod";
import useBrands from "../../hooks/use-brands";
import { JsonValue } from "@adora/shared/utils/patch";
import usePublishers from "../../hooks/use-publishers";

interface EditUserDialogProps extends BaseDialogProps {
  user: User;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ user, open, onClose }) => {
  const [username, setUsername] = useState<string>(user.name);
  const [password, setPassword] = useState<string>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { update: updateUser } = useMutateUser();
  const { brands: availableBrands } = useBrands();
  const { publishers } = usePublishers();
  const [selectedBrands, setSelectedBrands] = useState<string[]>(user.brands?.map(String) || []);
  const [brandError, setBrandError] = useState<string>("");
  const [externalInfo, setExternalInfo] = useState<string>(
    user.externalInfo ? JSON.stringify(user.externalInfo, null, 2) : ""
  );
  const [jsonError, setJsonError] = useState<string>("");
  const [email, setEmail] = useState<string>(user.email || "");

  const isAdmin = user.role === "internal" && user.accountType === "internal";
  const isMedia = user.accountType === "media";

  const publisherName = useMemo(() => {
    if (!isMedia || !user.rmnPublisherId || !publishers) return "";
    const publisher = publishers.find((p) => p.id === user.rmnPublisherId);
    return publisher?.name || "";
  }, [isMedia, user.rmnPublisherId, publishers]);

  const handleBrandChange = (brandId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedBrands((prev) => (event.target.checked ? [...prev, brandId] : prev.filter((id) => id !== brandId)));
  };

  const handleExternalInfoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setExternalInfo(value);

    // Validate JSON if there's content
    if (value.trim()) {
      try {
        JSON.parse(value);
        setJsonError("");
      } catch {
        setJsonError("Invalid JSON format");
      }
    } else {
      setJsonError("");
    }
  };

  const handleUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setBrandError("");
    setJsonError("");

    if (!isAdmin && !isMedia) {
      const brandSchema = z.object({
        brands: z.array(z.string()).min(1, "Please select at least one brand")
      });

      const result = brandSchema.safeParse({ brands: selectedBrands });
      if (!result.success) {
        setBrandError(result.error.issues[0].message);
        setSubmitting(false);
        return;
      }
    }

    // Parse and validate JSON before submitting
    let parsedExternalInfo: JsonValue | undefined;
    if (externalInfo.trim()) {
      try {
        parsedExternalInfo = JSON.parse(externalInfo) as JsonValue;
      } catch {
        setJsonError("Invalid JSON format");
        setSubmitting(false);
        return;
      }
    }

    await updateUser({
      id: user.id,
      name: username,
      password,
      brands: isMedia ? undefined : selectedBrands,
      externalInfo: parsedExternalInfo,
      email: isMedia ? email.trim() || undefined : undefined
    });

    setSubmitting(false);
    onClose(true);
  };

  return (
    <FormDialog
      title="Edit User"
      description="Edit user information"
      size="sm"
      open={open}
      onClose={onClose}
      onSubmit={handleUpdate}
      primaryButtonText="Update"
      isPrimaryButtonDisabled={submitting}
      dataDogIdentifier="edit user"
    >
      <Flex flexDirection="column" gap="8px">
        <Typography variant="body1">User Name</Typography>
        <TextField
          type="text"
          name="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username"
          required
          fullWidth
          dataDogIdentifier="edit user username"
        />
        <Typography variant="body1">New Password (optional)</Typography>
        <TextField
          type="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New Password"
          size="medium"
          fullWidth
          dataDogIdentifier="edit user password"
        />

        <Typography variant="body1" pt="16px">
          External Info (JSON)
        </Typography>
        <TextField
          type="text"
          name="externalInfo"
          value={externalInfo}
          onChange={handleExternalInfoChange}
          placeholder='{"key": "value"}'
          size="medium"
          fullWidth
          multiline
          rows={4}
          error={!!jsonError}
          helperText={jsonError || "Enter valid JSON data"}
          dataDogIdentifier="edit user external info"
        />

        {isMedia && (
          <>
            <Typography variant="body1" pt="24px" pb="8px">
              Publisher ID
            </Typography>
            <TextField
              type="text"
              name="publisherId"
              value={publisherName || user.rmnPublisherId?.toString() || ""}
              disabled
              size="medium"
              fullWidth
              dataDogIdentifier="edit user publisher id"
            />
            <Typography variant="body1" pt="24px" pb="8px">
              Email (optional)
            </Typography>
            <TextField
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              size="medium"
              fullWidth
              dataDogIdentifier="edit user email"
            />
          </>
        )}

        {!isAdmin && !isMedia && (
          <>
            <Typography variant="body1" pt="24px" pb="8px">
              Brands
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "12px",
                alignSelf: "stretch",
                borderRadius: "8px",
                border: brandError ? "1px solid red" : "1px solid rgba(0, 0, 0, 0.23)",
                background: "rgba(168, 171, 185, 0.05)",
                padding: "12px 16px"
              }}
            >
              <FormControl>
                <FormGroup>
                  {availableBrands?.map((brand) => (
                    <FormControlLabel
                      key={brand.id}
                      control={
                        <Checkbox
                          checked={selectedBrands.includes(brand.id.toString())}
                          onChange={handleBrandChange(brand.id.toString())}
                        />
                      }
                      label={brand.name}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
            {brandError && (
              <Typography color="error" variant="caption" pt="8px">
                {brandError}
              </Typography>
            )}
          </>
        )}
      </Flex>
    </FormDialog>
  );
};

export default EditUserDialog;
